import React from "react";
import { Sun, Moon, Globe } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Compact theme + language switcher.
 *
 * Variants:
 *   - "header": rounded chip, used in TopHeader and Login form panel
 *   - "ghost":  transparent, white text – for use on the dark brand panel
 */
export default function LocaleThemeSwitch({ variant = "header" }) {
  const { locale, setLocale, t } = useI18n();
  const { theme, toggle } = useTheme();

  const isHeader = variant === "header";
  const btnBase =
    "inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B7E8C]/40";
  const styleByVariant = isHeader
    ? "border border-[#E5E7EB] text-[#1F2937] bg-white hover:bg-[#F5F7FA]"
    : "border border-white/25 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm";

  return (
    <div className="flex items-center gap-2" data-testid="locale-theme-switch">
      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggle}
        title={theme === "dark" ? t("Switch to light mode") : t("Switch to dark mode")}
        data-testid="theme-toggle"
        className={`${btnBase} ${styleByVariant} w-9 px-0`}
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Language menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="language-toggle"
            className={`${btnBase} ${styleByVariant}`}
            title={t("Language")}
          >
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">
              {locale === "ar" ? "العربية" : "English"}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel>{t("Language")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="language-en"
            onClick={() => setLocale("en")}
            className={locale === "en" ? "bg-[#EAF8FA] text-[#0B7E8C] font-medium" : ""}
          >
            English
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="language-ar"
            onClick={() => setLocale("ar")}
            className={locale === "ar" ? "bg-[#EAF8FA] text-[#0B7E8C] font-medium" : ""}
          >
            العربية
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
